/**
 * Shared "PA status bucket" row helpers for the Eligible PA Statuses table,
 * used by the Text Notes and Letter Upload tabs.
 *
 * `documentType` / `documentName` are only populated for Letter Upload (sourced
 * from `document_config.documents`); Text Notes leaves them undefined.
 */
import { v4 as uuidv4 } from "uuid";
import { PaStatusTitleOption } from "../../../../../data-model/deliveryStepsConfig";

/** One editable row in the Eligible PA Statuses table. */
export interface PaStatusRow {
  id: string;
  bucket: string;
  alias: string;
  includeDrugName: boolean;
  /** Letter Upload only: document type for this bucket's generated letter. */
  documentType?: string;
  /** Letter Upload only: filename template for this bucket's generated letter. */
  documentName?: string;
}

/**
 * Flattens the `pa_status_title_options` object into table rows.
 *
 * @param options - Firestore status → title-option map
 * @returns Rows with stable ids for React keys / inline editing
 */
export function paStatusOptionsToRows(
  options: Record<string, PaStatusTitleOption> | undefined,
): PaStatusRow[] {
  return Object.entries(options ?? {}).map(([bucket, option]) => ({
    id: uuidv4(),
    bucket,
    alias: option?.title_alias ?? "",
    includeDrugName: option?.include_drug_name ?? false,
  }));
}

/**
 * Rebuilds the `pa_status_title_options` object from table rows, dropping rows
 * with a blank bucket and normalizing empty aliases to `null`.
 *
 * @param rows - Current table rows
 * @returns Firestore status → title-option map
 */
export function rowsToPaStatusOptions(
  rows: PaStatusRow[],
): Record<string, PaStatusTitleOption> {
  const result: Record<string, PaStatusTitleOption> = {};
  for (const row of rows) {
    const bucket = row.bucket.trim();
    if (!bucket) continue;
    const alias = row.alias.trim();
    result[bucket] = {
      title_alias: alias === "" ? null : alias,
      include_drug_name: row.includeDrugName,
    };
  }
  return result;
}

/** Creates a PA status row from modal / form input. */
export function createPaStatusRow(data: {
  bucket: string;
  alias?: string;
  includeDrugName: boolean;
  documentType?: string;
  documentName?: string;
}): PaStatusRow {
  return {
    id: uuidv4(),
    bucket: data.bucket,
    alias: data.alias ?? "",
    includeDrugName: data.includeDrugName,
    documentType: data.documentType,
    documentName: data.documentName,
  };
}

/** Creates a blank PA status row for the "Add new status" action. */
export function createEmptyPaStatusRow(): PaStatusRow {
  return {
    id: uuidv4(),
    bucket: "",
    alias: "",
    includeDrugName: false,
    documentType: "",
    documentName: "",
  };
}
